import { ButtonGhost, Colors, Icon, Text, cardTransition, isIconName } from '@amelio/ui-library';

import type { PriorityAttribute } from '@/types/priorityAttributeType';

interface PriorityOptionProps {
    /** Attribute data to display */
    attribute: PriorityAttribute;
    /** Whether the item is being dragged */
    isDragging?: boolean;
    /** Test ID for querying in tests */
    dataTestId?: string;
    onRemove?: () => void;
    removeLabel?: string;
}

const PriorityOption = ({
    attribute,
    isDragging = false,
    dataTestId = 'priority-option',
    onRemove,
    removeLabel,
}: PriorityOptionProps) => {
    const isActive = isDragging;

    return (
        <div
            data-testid={dataTestId}
            className={`flex w-full items-center gap-3 sm:min-w-[200px] ${cardTransition} group ${isActive ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
            <div className={'flex shrink-0 items-center justify-center'}>
                {/* A 9x15px 2x3 dot grid: non-square, so the Icon component's square size steps
                    can't reproduce it — rendered as literal dots instead. */}
                <span
                    aria-hidden
                    data-testid={`${dataTestId}-grip`}
                    className={'grid shrink-0 grid-cols-2 gap-1 px-0.5'}
                >
                    {Array.from({ length: 6 }).map((_, index) => (
                        <span
                            key={index}
                            data-testid={`${dataTestId}-grip-dot`}
                            className={'bg-primary h-1 w-1 rounded-full'}
                        />
                    ))}
                </span>
            </div>

            <div
                className={
                    'bg-base-25 group/card flex w-full min-w-0 flex-1 items-start gap-4 rounded-xl px-4 py-4'
                }
            >
                <div
                    className={
                        'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white'
                    }
                >
                    <Icon
                        name={
                            isIconName(attribute.iconName)
                                ? attribute.iconName
                                : 'essential-info-circle'
                        }
                        size={'sm'}
                        color={Colors.BASE_300}
                    />
                </div>

                <div className={'flex min-w-0 flex-1 flex-col'}>
                    <Text
                        weight={'semibold'}
                        size={'small'}
                        className={'leading-tight wrap-break-word'}
                        color={Colors.BASE}
                    >
                        {attribute.title}
                    </Text>

                    <Text
                        weight={'regular'}
                        size={'small'}
                        className={'text-xs leading-snug wrap-break-word'}
                        color={Colors.BASE_300}
                    >
                        {attribute.description}
                    </Text>
                </div>

                {onRemove && (
                    <span
                        className={
                            'group/remove shrink-0 opacity-0 transition-opacity group-hover:opacity-100'
                        }
                        onPointerDown={(event) => event.stopPropagation()}
                    >
                        <ButtonGhost
                            size={'sm'}
                            ariaLabel={removeLabel}
                            dataTestId={`${dataTestId}-remove`}
                            onClick={onRemove}
                        >
                            <span
                                className={
                                    'text-base-300 group-hover/remove:text-error transition-colors'
                                }
                            >
                                <Icon
                                    name={'essential-close'}
                                    size={'sm'}
                                    color={'inherit'}
                                />
                            </span>
                        </ButtonGhost>
                    </span>
                )}
            </div>
        </div>
    );
};

export { PriorityOption };
export type { PriorityOptionProps };
export default PriorityOption;
